import React, { useEffect, useState } from 'react';
import AppRouter from './Router';
import { AppBar, Toolbar, Typography, Container, Box, Menu, MenuItem, Avatar, Tooltip, IconButton, ListItemIcon, Button } from '@mui/material';
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web';
import keycloak from './keycloak';
import { Logout } from '@mui/icons-material';
import { useNavigate } from "react-router-dom";

const App: React.FC = () => {
  const { keycloak, initialized } = useKeycloak();
  const [userName, setUserName] = useState<string>('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (initialized) {
      if (keycloak?.authenticated) {
        // Set localStorage after successful authentication
        localStorage.setItem('autoGraderToken', keycloak.token || '');

        const user = keycloak.tokenParsed;
        if (user) {
          localStorage.setItem('user', user.email || '');
          const firstName = user.given_name || '';
          const lastName = user.family_name || '';
          setUserName(`${firstName} ${lastName}`);
        } else {
          setUserName('');
        }
      } else {
        setUserName('');
      }
      setLoading(false);
    }
  }, [initialized, keycloak]);

  useEffect(() => {
    if (initialized && !keycloak?.authenticated) {
      keycloak?.login();
    }
  }, [initialized, keycloak]);

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ flexGrow: 1 }} >
            <Box width={"150px"} position={"relative"}>
              <img src="https://www.upgrad.com/_ww3-next/_next/static/media/upgrad-header-logo.325f003e.svg" alt="upGrad logo" />
              <Typography ml={2} fontSize={14} sx={{ position: "absolute", bottom: -10, right: 10 }} fontWeight={700}>
                Autograder Tool
              </Typography>
            </Box>
          </Box>
          {keycloak?.authenticated && userName && (
            <>
             <Box display="flex" justifyContent="flex-end" mr={2}>
              <Button onClick={() => (window.location.href = "/performanceDashboard")} variant="outlined">
                Performance Dashboard
              </Button>
            </Box>
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                    {userName ? userName.split(' ').map((name) => name[0].toUpperCase()).join('') : 'UU'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      '& .MuiAvatar-root': {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                      },
                      '&::before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={handleClose}>
                  <Avatar /> {userName}
                </MenuItem>
                <MenuItem onClick={() => keycloak?.logout()}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Container maxWidth={false}>
        {keycloak?.authenticated ? <AppRouter /> : <p>Redirecting to Keycloak...</p>}
      </Container>
    </>
  );
};

const keycloakConfig: any = {
  authClient: keycloak,
  onEvent: (event: any) => {
    if (event === 'onAuthenticated') {
      console.log('User authenticated');
    } else if (event === 'onTokenExpired') {
      console.log('Token expired');
    }
  },
};

const AppWithKeycloakProvider: React.FC = () => {
  return (
    <ReactKeycloakProvider {...keycloakConfig}>
      <App />
    </ReactKeycloakProvider>
  );
};

export default AppWithKeycloakProvider;
